import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  X,
  Calendar,
  Clock,
  User,
  Stethoscope,
  MapPin,
  Plus,
  UserCircle,
  Loader2,
  AlertCircle,
  Ban,
} from "lucide-react";
import { format } from "date-fns";
import { showWarning, showSuccess, showError } from "@/utils/toastHelpers";
import { getErrorMessage } from "@/utils/errorUtils";

// Service availability constraint enum (matches backend)
const ServiceAvailabilityConstraint = {
  NONE: "NONE",
  NO_DOCTORS_ASSIGNED: "NO_DOCTORS_ASSIGNED",
  NO_ROOMS_ASSIGNED: "NO_ROOMS_ASSIGNED",
  NO_DOCTORS_AVAILABLE: "NO_DOCTORS_AVAILABLE",
  NO_ROOMS_AVAILABLE: "NO_ROOMS_AVAILABLE",
  ALL_DOCTORS_BOOKED: "ALL_DOCTORS_BOOKED",
  ALL_ROOMS_BOOKED: "ALL_ROOMS_BOOKED",
  EQUIPMENT_NOT_AVAILABLE: "EQUIPMENT_NOT_AVAILABLE",
  DURATION_EXCEEDS_AVAILABILITY: "DURATION_EXCEEDS_AVAILABILITY",
  DOCTOR_ON_BREAK: "DOCTOR_ON_BREAK",
  ROOM_ON_BREAK: "ROOM_ON_BREAK",
  DOCTOR_UNAVAILABLE: "DOCTOR_UNAVAILABLE",
  ROOM_UNAVAILABLE: "ROOM_UNAVAILABLE",
};

export default function SlotBookingModal({
  isOpen,
  onClose,
  selectedSlot,
  onBookingCreate,
}) {
  const queryClient = useQueryClient();
  const [selectedService, setSelectedService] = useState("");
  const [selectedPatient, setSelectedPatient] = useState("");
  const [selectedRoom, setSelectedRoom] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState("");

  // Reset form when modal closes or opens with new slot
  useEffect(() => {
    if (!isOpen) {
      setSelectedService("");
      setSelectedPatient("");
      setSelectedRoom("");
      setSelectedDoctor("");
    }
  }, [isOpen, selectedSlot]);

  // Fetch services with availability status for the selected slot
  const {
    data: services,
    isLoading: servicesLoading,
    error: servicesError,
  } = useQuery({
    queryKey: ["services-availability", selectedSlot?.date, selectedSlot?.time],
    queryFn: () =>
      apiClient.get("/api/bookings/services-availability", {
        date: format(selectedSlot?.date, "yyyy-MM-dd"),
        startTime: selectedSlot?.time,
      }),
    enabled: isOpen && !!selectedSlot?.date && !!selectedSlot?.time,
  });

  // Fetch patients from API
  const {
    data: patients,
    isLoading: patientsLoading,
    error: patientsError,
  } = useQuery({
    queryKey: ["patients"],
    queryFn: () => apiClient.get("/api/patients"),
    enabled: isOpen,
  });

  // Get selected service data
  const selectedServiceData = services?.find((s) => s.id === selectedService);

  // Fetch available rooms for the selected service and slot
  const {
    data: availableRooms,
    isLoading: roomsLoading,
    error: roomsError,
  } = useQuery({
    queryKey: [
      "available-rooms",
      selectedService,
      selectedSlot?.date,
      selectedSlot?.time,
    ],
    queryFn: () =>
      apiClient.get("/api/bookings/available-rooms", {
        serviceId: selectedService,
        date: format(selectedSlot?.date, "yyyy-MM-dd"),
        startTime: selectedSlot?.time,
        endTime: calculateEndTime(
          selectedSlot?.time,
          selectedServiceData?.totalDurationMinutes || 30
        ),
      }),
    enabled:
      isOpen &&
      !!selectedService &&
      !!selectedSlot?.date &&
      !!selectedSlot?.time &&
      !!selectedServiceData?.requiresRoom,
  });

  // Pre-select room from slot click (room view) or default to first available room
  useEffect(() => {
    if (selectedSlot?.roomId && availableRooms?.some((r) => r.id === selectedSlot.roomId)) {
      setSelectedRoom(selectedSlot.roomId);
    } else if (availableRooms?.length > 0 && !selectedRoom) {
      setSelectedRoom(availableRooms[0].id);
    }
  }, [availableRooms, selectedRoom, selectedSlot?.roomId]);

  // Fetch available doctors when service and date are selected
  const {
    data: availableDoctors,
    isLoading: doctorsLoading,
    error: doctorsError,
  } = useQuery({
    queryKey: [
      "available-doctors",
      selectedService,
      selectedSlot?.date,
      selectedSlot?.time,
    ],
    queryFn: () =>
      apiClient.get("/api/bookings/available-doctors", {
        serviceId: selectedService,
        date: format(selectedSlot?.date, "yyyy-MM-dd"),
        startTime: selectedSlot?.time,
        endTime: calculateEndTime(
          selectedSlot?.time,
          selectedServiceData?.totalDurationMinutes || 30
        ),
      }),
    enabled:
      isOpen &&
      !!selectedService &&
      !!selectedSlot?.date &&
      !!selectedSlot?.time &&
      !!selectedServiceData?.requiresDoctor,
  });

  // Booking creation mutation
  const createBookingMutation = useMutation({
    mutationFn: (bookingData) => apiClient.post("/api/bookings", bookingData),
    onSuccess: (data) => {
      showSuccess("Appointment booked successfully");
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["calendar"] });
      queryClient.invalidateQueries({ queryKey: ["available-doctors"] });
      queryClient.invalidateQueries({ queryKey: ["available-rooms"] });
      if (onBookingCreate) {
        onBookingCreate(data);
      }
      handleClose();
    },
    onError: (error) => {
      showError("Failed to book appointment", getErrorMessage(error));
    },
  });

  // Helper to calculate end time
  function calculateEndTime(startTime, duration) {
    if (!startTime || !duration) return null;
    const [hours, minutes] = startTime.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes + duration;
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`;
  }

  if (!isOpen || !selectedSlot) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedService || !selectedPatient) {
      showWarning("Please select both a service and patient");
      return;
    }

    if (selectedServiceData?.requiresDoctor && !selectedDoctor) {
      showWarning("Please select a doctor for this service");
      return;
    }

    const selectedPatientData = patients?.find(
      (p) => p.id === selectedPatient
    );

    if (!selectedPatientData) {
      showWarning("Invalid patient selected");
      return;
    }

    const duration = selectedServiceData?.totalDurationMinutes || 30;
    const endTime = calculateEndTime(selectedSlot.time, duration);

    const bookingData = {
      serviceId: selectedService,
      patientEmail: selectedPatientData.email,
      patientIdentificationNumber: selectedPatientData.identificationNumber,
      patientId: selectedPatient,
      date: format(selectedSlot.date, "yyyy-MM-dd"),
      startTime: selectedSlot.time,
      endTime: endTime,
      doctorId: selectedDoctor || undefined,
      roomId: selectedRoom || undefined,
    };

    createBookingMutation.mutate(bookingData);
  };

  const handleClose = () => {
    setSelectedService("");
    setSelectedPatient("");
    setSelectedRoom("");
    setSelectedDoctor("");
    onClose();
  };

  const isLoading = servicesLoading || patientsLoading;
  const hasError = servicesError || patientsError;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex justify-between items-start">
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Book Appointment
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Selected Time Slot Info */}
          <div className="bg-blue-50 p-3 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {format(selectedSlot.date, "EEEE, MMMM d, yyyy")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {selectedSlot.time}
              </span>
            </div>
          </div>

          {/* Error State */}
          {hasError && (
            <div className="bg-red-50 p-3 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">
                Failed to load data. Please try again.
              </span>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-sm text-gray-600">Loading...</span>
            </div>
          )}

          {!isLoading && !hasError && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Service Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Stethoscope className="w-4 h-4" />
                  Service *
                </label>
                {services?.filter((service) => service.status === "active")
                  .length === 0 ? (
                  <div className="text-sm text-amber-600 p-2 bg-amber-50 rounded">
                    No active services available. Please create a service in
                    Settings first.
                  </div>
                ) : (
                  <Select
                    value={selectedService}
                    onValueChange={(value) => {
                      const service = services?.find((s) => s.id === value);
                      if (service?.isAvailableForSlot !== false) {
                        setSelectedService(value);
                        setSelectedDoctor("");
                        setSelectedRoom("");
                      }
                    }}
                    disabled={createBookingMutation.isPending}
                  >
                    <SelectTrigger data-testid="slot-booking-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services
                        ?.filter((service) => service.status === "active")
                        .map((service, index) => {
                          const isUnavailable = service.isAvailableForSlot === false;
                          return (
                            <SelectItem
                              key={service.id}
                              value={service.id}
                              data-testid={`slot-booking-service-option-${index}`}
                              disabled={isUnavailable}
                              className={isUnavailable ? "opacity-60" : ""}
                            >
                              <div className="flex flex-col w-full">
                                <div className="flex justify-between items-center w-full">
                                  <span className={isUnavailable ? "text-gray-400" : ""}>
                                    {service.name}
                                  </span>
                                  <Badge
                                    variant={isUnavailable ? "outline" : "secondary"}
                                    className={`ml-2 ${isUnavailable ? "text-gray-400 border-gray-300" : ""}`}
                                  >
                                    {service.totalDurationMinutes}min
                                  </Badge>
                                </div>
                                {isUnavailable && (
                                  <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
                                    <Ban className="w-3 h-3" />
                                    <span>{service.constraintDescription}</span>
                                  </div>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Patient Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Patient *
                </label>
                {!patients || patients.length === 0 ? (
                  <div className="text-sm text-amber-600 p-2 bg-amber-50 rounded">
                    No patients found. Please add a patient first.
                  </div>
                ) : (
                  <Select
                    value={selectedPatient}
                    onValueChange={setSelectedPatient}
                    disabled={createBookingMutation.isPending}
                  >
                    <SelectTrigger data-testid="slot-booking-patient-select">
                      <SelectValue placeholder="Select a patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients?.map((patient, index) => (
                        <SelectItem key={patient.id} value={patient.id} data-testid={`slot-booking-patient-option-${index}`}>
                          <div className="flex flex-col items-start">
                            <span>{patient.name}</span>
                            <span className="text-xs text-gray-500">
                              {patient.email}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Doctor Selection - Only shown for services requiring a doctor */}
              {selectedServiceData?.requiresDoctor && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <UserCircle className="w-4 h-4" />
                    Doctor *
                  </label>
                  {doctorsLoading ? (
                    <div className="flex items-center gap-2 p-2 text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading doctors...
                    </div>
                  ) : doctorsError ? (
                    <div className="text-sm text-red-600 p-2">
                      Failed to load doctors
                    </div>
                  ) : availableDoctors?.length === 0 ? (
                    <div className="text-sm text-amber-600 p-2 bg-amber-50 rounded">
                      No active doctors available for this time slot
                    </div>
                  ) : (
                    <Select
                      value={selectedDoctor}
                      onValueChange={setSelectedDoctor}
                      disabled={createBookingMutation.isPending}
                    >
                      <SelectTrigger data-testid="slot-booking-doctor-select">
                        <SelectValue placeholder="Select a doctor" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDoctors?.map((doctor, index) => (
                          <SelectItem
                            key={doctor.id}
                            value={doctor.id}
                            disabled={doctor.status === "invited"}
                            data-testid={`slot-booking-doctor-option-${index}`}
                          >
                            <div className="flex items-center gap-2">
                              <span>
                                {doctor.firstName} {doctor.lastName}
                              </span>
                              {doctor.status === "invited" && (
                                <Badge
                                  variant="outline"
                                  className="text-xs text-amber-600 border-amber-400"
                                >
                                  Pending Invitation
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Room Selection - Only shown for services requiring a room */}
              {selectedServiceData?.requiresRoom && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Room *
                  </label>
                  {roomsLoading ? (
                    <div className="flex items-center gap-2 p-2 text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading rooms...
                    </div>
                  ) : roomsError ? (
                    <div className="text-sm text-red-600 p-2">
                      Failed to load rooms
                    </div>
                  ) : availableRooms?.length === 0 ? (
                    <div className="text-sm text-amber-600 p-2 bg-amber-50 rounded">
                      No rooms available for this service and time slot.
                      Please assign rooms to this service in Settings.
                    </div>
                  ) : (
                    <Select
                      value={selectedRoom}
                      onValueChange={setSelectedRoom}
                      disabled={createBookingMutation.isPending}
                    >
                      <SelectTrigger data-testid="slot-booking-room-select">
                        <SelectValue placeholder="Select a room" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRooms?.map((room, index) => (
                          <SelectItem key={room.id} value={room.id} data-testid={`slot-booking-room-option-${index}`}>
                            <div className="flex flex-col items-start">
                              <span>{room.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                  disabled={createBookingMutation.isPending}
                  data-testid="slot-booking-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={
                    createBookingMutation.isPending ||
                    isLoading ||
                    (selectedServiceData?.requiresDoctor && !selectedDoctor) ||
                    services?.filter((s) => s.status === "active").length ===
                      0 ||
                    !patients ||
                    patients.length === 0 ||
                    (selectedServiceData?.requiresRoom && !selectedRoom)
                  }
                  data-testid="slot-booking-submit-btn"
                >
                  {createBookingMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Booking...
                    </>
                  ) : (
                    "Book Appointment"
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
